import { CostPriceIngredient } from "../../../../services/costPrice/costPriceService";
import { SubrecipeIngredient } from "../../../../services/subrecipes/subrecipesService";
import { ReviewIngredient, SubrecipeReviewItem } from "../../../../types/reviewTypes";
import { Platform } from "../../../../types/Platforms";
import { applyWaste, resolveWaste } from "../../../../utils/wasteUtils";
import { resolveProductIdentifier } from "../../../../utils/platformUtils";

/**
 * Live cost of ONE enriched analysis row, mirroring the per-row math inside
 * `enrichCostPriceRecursive`'s base-cost reduce: subrecipe rows price per portion
 * unit, nested cost-price rows per portion of the nested dish, and plain product
 * rows are waste-inflated. Returns null when the row has no live price.
 */
export function ingredientLineCost(
    row: CostPriceIngredient | undefined,
    defaultWastePercentage: number
): number | null {
    if (!row || row.currentPrice === undefined) return null;
    if (row.subrecipeId) return row.currentPrice * (row.subrecipePortionsNeeded ?? 0);
    if (row.nestedCostPriceId) return row.currentPrice * (row.nestedQuantityNeeded ?? 1);
    if (row.quantityNeeded === undefined) return null;
    const waste = resolveWaste(row.wastePercentage, defaultWastePercentage);
    return applyWaste(row.quantityNeeded, waste) * row.currentPrice;
}

/** Confidence threshold shared by the retained subrecipe import review. */
const LOW_CONFIDENCE = 0.6;

export function isKnownPlatform(platformName?: string): boolean {
    const validPlatforms = Object.values(Platform).map(p => p.toString().toLowerCase());
    return validPlatforms.includes(platformName?.toLowerCase() || '');
}

type UnitType = 'weight' | 'volume' | 'count' | 'unknown';

/** Mirrors the server-side `getUnitType` in ingredientSearchHelper.ts. */
function getUnitType(unit: string): UnitType {
    const u = unit.toLowerCase().trim();
    if (['g', 'gram', 'grams', 'kg', 'kilogram', 'kilograms'].includes(u)) return 'weight';
    if (['ml', 'milliliter', 'milliliters', 'l', 'liter', 'liters', 'cl', 'dl'].includes(u)) return 'volume';
    // 'unit' is a COUNT unit, not an unknown one. It is a real member of the inventory
    // `StdUnit` union (`server/utils/inventoryUnitHelper.ts:4`), written by the Excel import
    // when no unit could be parsed — i.e. "a countable item", exactly what 'p' means. Order
    // history only ever carries g/ml/p (`emailData/orderHistory.ts:128-132`), so 'unit' reaches
    // here from custom ingredients. Treating it as unknown made `unit -> p` a type change, which
    // sent `defaultQuantityFor` to its 100/1 default and turned "0.05 of a bottle" into "1".
    // Mirrors the server twin's `getUnitType` (`automationHelper/ingredientSearchHelper.ts:350`);
    // the two tables must agree.
    if (['p', 'piece', 'pieces', 'stuk', 'stuks', 'st', 'portion', 'portions', 'unit', 'units'].includes(u)) return 'count';
    return 'unknown';
}

/** Factor converting `unit` into the base std unit of its type (g / ml / piece). */
const TO_BASE: Record<string, number> = {
    kg: 1000, kilogram: 1000, kilograms: 1000,
    l: 1000, liter: 1000, liters: 1000, dl: 100, cl: 10,
};

/**
 * Convert the template's quantity (e.g. "0.12 kg") into the target product's
 * standard unit (e.g. 120 when the product is priced per gram). Returns null
 * when the unit types don't match (e.g. grams → pieces) — the caller then falls
 * back to a per-unit default instead of persisting a nonsensical number.
 */
export function templateQuantityInUnit(
    templateQuantity: number,
    templateUnit: string,
    targetUnit: string | undefined
): number | null {
    if (!targetUnit || !(templateQuantity > 0)) return null;
    const templateType = getUnitType(templateUnit);
    const targetType = getUnitType(targetUnit);
    if (templateType === 'unknown' || targetType === 'unknown' || templateType !== targetType) return null;

    const factor = (TO_BASE[templateUnit.toLowerCase().trim()] ?? 1) / (TO_BASE[targetUnit.toLowerCase().trim()] ?? 1);
    const converted = templateQuantity * factor;
    // Trim float noise (0.12 * 1000 = 120.00000000000001).
    return Math.round(converted * 1e6) / 1e6;
}

/**
 * Match a live subrecipe ingredient row against a review-report ingredient.
 * The subrecipe import is nesting-free, so a nested-subrecipe row can never be
 * what a template line produced and is always skipped. Products match on
 * platform + canonical product identity, falling back to lowercased productName.
 */
export function matchesSubrecipeRow(row: SubrecipeIngredient, ingredient: ReviewIngredient): boolean {
    if (row.nestedSubrecipeId) return false;
    if (!ingredient.platform || row.platform !== ingredient.platform) return false;
    // Not raw article ids: unrelated products can share a catch-all id, and the import
    // adds rows next to existing ones that a remove/swap must not take with it.
    const identifier = resolveProductIdentifier(ingredient.platform, ingredient.articleId, ingredient.productName);
    if (identifier !== null && identifier === resolveProductIdentifier(ingredient.platform, row.articleId, row.productName)) return true;
    return !!ingredient.productName && row.productName?.toLowerCase() === ingredient.productName.toLowerCase();
}

/**
 * Analysis rows are deduped, so two report ingredients can share one row. A
 * shared row must survive a remove/swap of one entry as long as another
 * still-active (not removed) entry of the same subrecipe maps to it.
 */
export function isSubrecipeRowShared(
    row: SubrecipeIngredient,
    ingredients: ReviewIngredient[] | undefined,
    ingredientIndex: number
): boolean {
    if (!ingredients) return false;
    return ingredients.some((other, i) =>
        i !== ingredientIndex &&
        other.reviewAction !== 'removed' &&
        matchesSubrecipeRow(row, other)
    );
}

/**
 * Whether a subrecipe still has something the reviewer should look at: an
 * unmatched ingredient, a low-confidence match that hasn't been touched, or a
 * yield the automation had to guess.
 */
export function subrecipeNeedsAttention(item: SubrecipeReviewItem): boolean {
    if (item.portionsWasGuessed) return true;
    return item.ingredients.some(ingredient => {
        if (ingredient.reviewAction) return false;
        if (ingredient.status === 'unmatched') return true;
        if (ingredient.status === 'exact') return false;
        return (ingredient.confidence ?? 0) < LOW_CONFIDENCE;
    });
}
